'use client';

import { FormGroupSlot } from '@magicschool/spellbook/components/Form/FormGroup';
import {
  FormAutocompleteSlot,
  FormCheckboxGroupSlot,
  FormCheckboxSlot,
  FormDateInputSlot,
  FormErrorMessageSlot,
  FormFooterSlot,
  FormHelperTextSlot,
  FormLabelSlot,
  FormMultiSelectSlot,
  FormNumberInputSlot,
  FormRadioGroupSlot,
  FormSearchInputSlot,
  FormSelectSlot,
  FormSwitchGroupSlot,
  FormSwitchSlot,
  FormTextareaSlot,
  FormTextInputSlot,
  FormTooltipSlot,
} from '@magicschool/spellbook/components/Form/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { ComponentProps } from 'react';
import { forwardRef } from 'react';

export type FormProps = ComponentProps<'form'>;

const FormBase = forwardRef<HTMLFormElement, FormProps>(({ className, ...props }, ref) => {
  return <form ref={ref} data-spellbook-name="form" className={cn('flex flex-col gap-6 w-full', className)} {...props} />;
});

FormBase.displayName = 'Form';

/**
 * A `<form>` whose fields are built from `Form.Group`s, each of which wires one control to its label, helper text and error message.
 *
 * ### When to use
 * - Any set of labelled inputs the user fills in and submits: settings, sign-up, tool inputs
 * - A single labelled field that needs helper text, an error message or a footer, even outside a larger form
 *
 * ### When NOT to use
 * - A standalone control with no label, helper text or validation (a toolbar search box): use the control directly, e.g. `SearchInput`
 *
 * ### API
 * - `Form` takes every native `<form>` prop (`onSubmit`, `noValidate`, `action`, ...)
 * - `Form.Group` props apply to its control: `name`, `required`, `disabled`, `invalid`, and `controlId` to fix the control's `id`
 *
 * ### Composition (slots)
 * - `<Form.Group>`: One field. Detects the single `Form.*` control among its direct children and lays out the slots below. Always include it.
 * - `<Form.Label>`: Renders `Label`, linked to the control, with an asterisk when the Group is `required`. Include for every control except `Form.Checkbox`, `Form.Switch` and `Form.SearchInput`.
 * - `<Form.Tooltip>`: Renders an info `IconButton` with a `Tooltip` next to the label. The button is named by `text.trigger` (default "More information"). Only include for extra context.
 * - `<Form.HelperText>`: Renders muted `Text`. Below the control by default, above group controls; `position` overrides. Below the control, it gives way to the error message while the Group is `invalid`.
 * - `<Form.ErrorMessage>`: Renders an icon and error `Text` in an `aria-live` region. Include alongside `invalid` on the Group.
 * - `<Form.Footer>`: End-aligned row below the control for counters or actions. `<Form.Footer.Text>` inside it is added to the control's description.
 * - Controls: `<Form.TextInput>`, `<Form.Textarea>`, `<Form.Select>`, `<Form.MultiSelect>`, `<Form.Autocomplete>`, `<Form.SearchInput>`, `<Form.DateInput>`, `<Form.NumberInput>`, `<Form.Checkbox>`, `<Form.Switch>`, `<Form.CheckboxGroup>`, `<Form.RadioGroup>`, `<Form.SwitchGroup>`. Each renders the matching component and exposes its slots (`Form.TextInput.LeadingAddOn`).
 *
 * ### Visual states
 * - **Required**: Label shows an asterisk; the control gets `required`
 * - **Invalid**: Control shows error styling and `aria-invalid`; `Form.ErrorMessage` replaces the helper text
 * - **Disabled**: Control is disabled
 *
 * ### Accessibility
 * - The Group generates the control `id` and links the label with `htmlFor`
 * - `aria-describedby` on the control lists the helper text, error message and `Form.Footer.Text`, after any ids you set yourself
 * - `Form.ErrorMessage` is an `aria-live="polite"` region, so new errors are announced
 *
 * @example
 * ```tsx
 * <Form onSubmit={onSubmit}>
 *   <Form.Group name="email" required>
 *     <Form.Label>Email</Form.Label>
 *     <Form.TextInput type="email" />
 *     <Form.HelperText>We'll never share it.</Form.HelperText>
 *   </Form.Group>
 * </Form>
 * ```
 */
export const Form = Object.assign(FormBase, {
  Group: FormGroupSlot,
  Label: FormLabelSlot,
  Tooltip: FormTooltipSlot,
  HelperText: FormHelperTextSlot,
  ErrorMessage: FormErrorMessageSlot,
  Footer: FormFooterSlot,
  Autocomplete: FormAutocompleteSlot,
  Checkbox: FormCheckboxSlot,
  CheckboxGroup: FormCheckboxGroupSlot,
  DateInput: FormDateInputSlot,
  MultiSelect: FormMultiSelectSlot,
  NumberInput: FormNumberInputSlot,
  RadioGroup: FormRadioGroupSlot,
  SearchInput: FormSearchInputSlot,
  Select: FormSelectSlot,
  Switch: FormSwitchSlot,
  SwitchGroup: FormSwitchGroupSlot,
  TextInput: FormTextInputSlot,
  Textarea: FormTextareaSlot,
});
